import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import VpChevronDownIcon from '@vtmn-play/icons/react/VpChevronDownIcon';

import { cleanBeforeReload } from '../../../utils/redirectToFed';
import { selectAppLanguage, selectAvailableLanguages } from '../../../store/selectors/i18n.selectors';
import { type Locale } from '../../../store/i18nSlice';
import { ButtonDropdown } from '../../shared/Dropdowns/ButtonDropdown';
import { Flags } from '../../shared/Flags/Flags';
import { selectSelectedSite } from '../../../store/selectors/userInfo';
import { LocaleDropdownItem } from './LocaleDropdownItem';
import { TertiaryButton } from '../../shared/Buttons/TertiaryButton';

const defaultLocales = new Set(['fr-FR', 'en-GB']);

interface SelectedLocale extends Locale {
  selected: boolean;
}

export const LocaleDropdown = () => {
  const [showOtherLanguages, setShowOtherLanguages] = useState(false);
  const appLanguage = useSelector(selectAppLanguage);
  const availableLanguages = useSelector(selectAvailableLanguages);
  const selectedSite = useSelector(selectSelectedSite);

  const siteLanguages: SelectedLocale[] = [];
  const defaultLanguages: SelectedLocale[] = [];
  const otherLanguages: SelectedLocale[] = [];

  availableLanguages.forEach(({ country, language }) => {
    const selected = language === appLanguage?.language && country === appLanguage?.country;
    const countryLanguage = { country, language, selected };
    if (country === selectedSite.address.countryCode) {
      siteLanguages.push(countryLanguage);
    } else if (defaultLocales.has(`${language.toLowerCase()}-${country.toUpperCase()}`)) {
      defaultLanguages.push(countryLanguage);
    } else {
      otherLanguages.push(countryLanguage);
    }
  });

  const handleClickOnFlag = (locale: Locale) => {
    if (appLanguage?.country === locale.country && appLanguage?.language === locale.language) {
      return;
    }
    void cleanBeforeReload(locale, null);
  };

  const renderLocales = (locales: SelectedLocale[], type: string) =>
    locales.map(locale => (
      <LocaleDropdownItem
        key={`${locale.country}-${locale.language}`}
        locale={locale}
        type={type}
        selected={locale.selected}
        onClick={handleClickOnFlag}
      />
    ));

  const handleDropdownClose = () => {
    setShowOtherLanguages(false);
  };

  return (
    <ButtonDropdown
      className="text-black"
      label={appLanguage?.language?.toUpperCase() ?? 'EN'}
      startSlot={<Flags lang={appLanguage?.country.toLowerCase() ?? 'gb'} />}
      data-testid="dropdown-language"
      onClose={handleDropdownClose}
      contentRole="menu"
      aria-label="Language"
    >
      {renderLocales(siteLanguages, 'site')}
      {siteLanguages.length > 0 && <hr />}
      {renderLocales(defaultLanguages, 'default')}
      <hr />
      {showOtherLanguages ? (
        <div id="other-languages" role="group" aria-label="Other languages">
          {renderLocales(otherLanguages, 'other')}
        </div>
      ) : (
        <TertiaryButton
          data-testid="dropdown-language-show-more"
          className="full-width text-black"
          onClick={() => setShowOtherLanguages(true)}
          shape="squared"
          aria-expanded={false}
          aria-controls="other-languages"
          aria-label="Show more languages"
        >
          <VpChevronDownIcon size={12} />
        </TertiaryButton>
      )}
    </ButtonDropdown>
  );
};
